/**
 * Blinkit-Style Quick Add
 * - Replaces ADD button with +/- quantity controls after adding
 * - Animates product flying to floating cart
 * - Real-time cart sync without page refresh
 */

(function() {
  'use strict';

  // Cart state management
  const CartState = {
    items: new Map(), // variantId -> { quantity, line }
    loading: new Set(),

    async init() {
      await this.fetchCart(true); // initial sync — buttons only, no publish (page is already server-rendered)
      this.setupListeners();
    },

    async fetchCart(silent = false) {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.items.clear();
        cart.items.forEach((item, index) => {
          this.items.set(String(item.id), {
            quantity: item.quantity,
            line: index + 1,
            image: (item.featured_image && item.featured_image.url) || item.image,
            title: item.product_title
          });
        });
        this.updateAllButtons();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        // Publish to Shopify pub/sub ONLY when this fetch reflects a change WE made.
        // When silent (initial load / syncing in reaction to someone else's cartUpdate)
        // we must NOT re-publish, otherwise cart-items re-renders a 2nd time = lag + flicker.
        if (!silent && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'blinkit-quick-add', cartData: cart });
        }
      } catch (e) {
        console.error('Cart fetch failed:', e);
      }
    },

    getQuantity(variantId) {
      const item = this.items.get(String(variantId));
      return item ? item.quantity : 0;
    },

    isLoading(variantId) {
      return this.loading.has(String(variantId));
    },

    updateAllButtons() {
      document.querySelectorAll('[data-blinkit-add]').forEach(btn => {
        const variantId = btn.dataset.variantId;
        const qty = this.getQuantity(variantId);
        this.updateButtonState(btn, qty);
      });
    },

    updateButtonState(container, qty) {
      const qtyDisplay = container.querySelector('.blinkit-qty-value');

      // Mark as initialized to prevent flash
      container.classList.add('blinkit-initialized');

      if (qty > 0) {
        container.classList.add('has-qty');
        if (qtyDisplay) qtyDisplay.textContent = qty;
      } else {
        container.classList.remove('has-qty');
      }
    },

    setupListeners() {
      // Listen for Shopify pub/sub cart updates from other sources (product-form, cart drawer +/-)
      // so blinkit buttons stay in sync when the theme's own cart JS makes changes
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'blinkit-quick-add') return; // skip our own events
          this.fetchCart(true); // silent sync — update buttons only, don't re-publish (breaks the cascade)
        });
      }
    }
  };

  // Quick Add Handler
  const BlinkitQuickAdd = {
    _lastAddTime: 0,

    async addToCart(variantId, quantity, imageUrl, triggerElement) {
      if (CartState.isLoading(variantId)) return;

      CartState.loading.add(String(variantId));
      this.setButtonLoading(variantId, true);

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const result = await response.json();

        // Trigger flying animation
        if (imageUrl && triggerElement) {
          if (typeof window.triggerFloatingCartAnimation === 'function') window.triggerFloatingCartAnimation(imageUrl, triggerElement);
        }

        // Add animate class for qty controls transition
        document.querySelectorAll(`[data-blinkit-add][data-variant-id="${variantId}"]`).forEach(c => {
          c.classList.add('animate-qty');
          setTimeout(() => c.classList.remove('animate-qty'), 300);
        });

        // INSTANT UPDATE: Run all updates in parallel, don't wait for each other
        const cartPromise = CartState.fetchCart();
        const drawerPromise = this.refreshCartDrawer();

        // Fire and forget - don't await, let them run in background
        Promise.all([cartPromise, drawerPromise]).catch(() => {});

      } catch (error) {
        console.error('Add to cart error:', error);
        this.showError(variantId, 'Failed to add');
      } finally {
        CartState.loading.delete(String(variantId));
        this.setButtonLoading(variantId, false);
      }
    },

    async updateQuantity(variantId, newQuantity, imageUrl, triggerElement) {
      if (CartState.isLoading(variantId)) return;

      CartState.loading.add(String(variantId));
      this.setButtonLoading(variantId, true);

      try {
        const item = CartState.items.get(String(variantId));

        if (newQuantity === 0) {
          // Remove from cart
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 0
            })
          });
        } else {
          // Update quantity
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: newQuantity
            })
          });

          // Trigger flying animation on increase
          const currentQty = CartState.getQuantity(variantId);
          if (newQuantity > currentQty && imageUrl && triggerElement) {
            if (typeof window.triggerFloatingCartAnimation === 'function') window.triggerFloatingCartAnimation(imageUrl, triggerElement);
          }
        }

        // INSTANT UPDATE: Run all updates in parallel
        const cartPromise = CartState.fetchCart();
        const drawerPromise = this.refreshCartDrawer();
        Promise.all([cartPromise, drawerPromise]).catch(() => {});

      } catch (error) {
        console.error('Update quantity error:', error);
      } finally {
        CartState.loading.delete(String(variantId));
        this.setButtonLoading(variantId, false);
      }
    },

    setButtonLoading(variantId, isLoading) {
      document.querySelectorAll(`[data-blinkit-add][data-variant-id="${variantId}"]`).forEach(container => {
        if (isLoading) {
          container.classList.add('loading');
        } else {
          container.classList.remove('loading');
        }
      });
    },

    showError(variantId, message) {
      console.error(message);
    },

    // Refresh cart drawer content. Single request — the cart-drawer section HTML
    // already contains the empty state + item count, so no extra /cart.js needed.
    refreshCartDrawer() {
      return fetch(`${(window.routes && window.routes.cart_url) || '/cart'}?section_id=cart-drawer`)
        .then(r => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cartDrawer = document.querySelector('cart-drawer');

          // Update drawer inner content
          const sourceDrawerInner = doc.querySelector('.drawer__inner');
          const targetDrawerInner = cartDrawer ? cartDrawer.querySelector('.drawer__inner') : null;

          if (sourceDrawerInner && targetDrawerInner) {
            targetDrawerInner.innerHTML = sourceDrawerInner.innerHTML;

            // Re-attach overlay click listener
            const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
            if (overlay) {
              overlay.addEventListener('click', () => cartDrawer.close());
            }
          }

          // Derive empty-state + item count from the same rendered HTML
          const sourceDrawer = doc.querySelector('cart-drawer');
          const isEmpty = sourceDrawer ? sourceDrawer.classList.contains('is-empty') : false;
          let itemCount = 0;
          if (!isEmpty) {
            const countEl = doc.getElementById('cart-item-count');
            itemCount = countEl ? (parseInt(countEl.textContent.trim(), 10) || 0) : null;
          }

          if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
          if (itemCount !== null) this.updateCartIcon(itemCount);
        }).catch(e => {
          console.error('Cart drawer refresh failed:', e);
        });
    },

    // Separate function for instant cart icon update (header + mobile bottom nav)
    updateCartIcon(itemCount) {
      // Update header cart icon bubble
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');

        if (itemCount > 0) {
          if (cartCountBubble) {
            const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
            if (countSpan) countSpan.textContent = itemCount;
          } else {
            const bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">${itemCount} items</span>`;
            cartIconBubble.appendChild(bubble);
          }
        } else {
          if (cartCountBubble) cartCountBubble.remove();
        }
      }

      // Update mobile bottom nav cart count
      const mobileCartBubble = document.querySelector('#cart--bubble');
      if (mobileCartBubble) mobileCartBubble.textContent = itemCount;
    }
  };

  // Event Delegation for all quick-add buttons
  document.addEventListener('click', function(e) {
    // ADD button click
    const addBtn = e.target.closest('.blinkit-add-btn');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();

      const container = addBtn.closest('[data-blinkit-add]');
      if (!container) return;

      const variantId = container.dataset.variantId;
      const imageUrl = container.dataset.imageUrl;
      const cardWrapper = container.closest('.card-wrapper');
      const imageEl = cardWrapper ? cardWrapper.querySelector('.card__media img') : null;

      BlinkitQuickAdd.addToCart(variantId, 1, imageUrl, imageEl || addBtn);
      return;
    }

    // Plus button click
    const plusBtn = e.target.closest('.blinkit-plus-btn');
    if (plusBtn) {
      e.preventDefault();
      e.stopPropagation();

      const container = plusBtn.closest('[data-blinkit-add]');
      if (!container) return;

      const variantId = container.dataset.variantId;
      const imageUrl = container.dataset.imageUrl;
      const currentQty = CartState.getQuantity(variantId);
      const cardWrapper = container.closest('.card-wrapper');
      const imageEl = cardWrapper ? cardWrapper.querySelector('.card__media img') : null;

      BlinkitQuickAdd.updateQuantity(variantId, currentQty + 1, imageUrl, imageEl || plusBtn);
      return;
    }

    // Minus button click
    const minusBtn = e.target.closest('.blinkit-minus-btn');
    if (minusBtn) {
      e.preventDefault();
      e.stopPropagation();

      const container = minusBtn.closest('[data-blinkit-add]');
      if (!container) return;

      const variantId = container.dataset.variantId;
      const currentQty = CartState.getQuantity(variantId);
      const newQty = Math.max(0, currentQty - 1);

      BlinkitQuickAdd.updateQuantity(variantId, newQty, null, null);
      return;
    }
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartState.init());
  } else {
    CartState.init();
  }

  // Re-init on Shopify section refresh
  document.addEventListener('shopify:section:load', () => CartState.fetchCart());

  // Re-sync on bfcache / back-forward navigation so buttons show correct state
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) CartState.fetchCart();
  });

  // Expose for external use
  window.BlinkitQuickAdd = BlinkitQuickAdd;
  window.BlinkitCartState = CartState;

})();
